import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  viewChild,
  ViewChild
} from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import {
  SortDto,
  TeamItemDto,
  TeamService,
} from '@artemis-software/wr-api';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  exhaustMap,
  Observable,
  of,
  startWith,
  Subject,
  tap,
} from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import OrderEnum = SortDto.OrderEnum;
import { NbPopoverDirective } from '@nebular/theme';

@Component({
  standalone: false,
  selector: 'wr-team-select',
  templateUrl: './team-select.component.html',
  styleUrls: ['./team-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TeamSelectComponent),
      multi: true,
    },
  ],
})
export class TeamSelectComponent extends AbstractFormControl<TeamItemDto> implements OnInit {

  readonly teamService = inject(TeamService);
  readonly router = inject(Router);

  popover = viewChild(NbPopoverDirective);

  @ViewChild('searchInput')
  input?: ElementRef;

  @Input()
  tenantId$?: Observable<string>;

  @Input()
  regionId$?: Observable<string>;

  @Input()
  fixedTenantId: string | undefined = undefined;

  @Output() teamSelected = new EventEmitter<TeamItemDto>();

  filterText$ = new BehaviorSubject<string>('');

  lastTenantId: string | undefined = undefined;

  lastRegionId: string | undefined = undefined;

  nextPage = 0;

  trigger$ = new Subject<'reset' | 'filter' | 'loadMore'>();

  teams$ = new BehaviorSubject<TeamItemDto[]>([]);

  ngOnInit(): void {
    combineLatest([
      this.filterText$.pipe(debounceTime(100)),
      this.tenantId$ ? this.tenantId$.pipe(startWith(undefined)) : of(undefined),
      this.regionId$ ? this.regionId$.pipe(startWith(undefined)) : of(undefined),
      this.trigger$,
    ])
      .pipe(
        exhaustMap(([filterText, tenantId, regionId, trigger]) => {
          let appliedFilterText: string | undefined = undefined;
          if (filterText?.length > 0) {
            appliedFilterText = filterText;
          }

          const filterTenantId = this.fixedTenantId || tenantId;

          if (this.lastRegionId !== regionId) {
            trigger = 'reset';
            this.lastRegionId = regionId;
          }
          if (this.lastTenantId !== filterTenantId) {
            trigger = 'reset';
            this.lastTenantId = filterTenantId;
          }
          if (trigger === 'reset') {
            this.nextPage = 0;
          }

          return this.teamService.findAll({
            tenantId: filterTenantId,
            regionId: regionId,
            text: appliedFilterText,
            page: this.nextPage,
            size: 10,
            sort: {
              sort: 'name',
              order: OrderEnum.Asc,
            },
          }).pipe(
            tap(teams => {
              if (trigger === 'reset') {
                this.teams$.next(teams);
              } else {
                const current = this.teams$.value || [];
                this.teams$.next([...current, ...teams]);
              }
              this.nextPage++;
            }),
          );
        })
      )
      .subscribe();
  }

  trackById(index: number, item: TeamItemDto): string {
    return item.id;
  }

  override writeValue(user: TeamItemDto): void {
    super.writeValue(user);
    this.filterText$.next(this.viewHandle(user));
  }

  onSelectionChange(user?: TeamItemDto): void {
    if (user?.id) {
      this.filterText$.next(this.viewHandle(user));
      this.value = user;
      this.popover()?.hide();
      this.teamSelected.emit(user);
    }
  }

  viewHandle(value?: TeamItemDto | string): string {
    if (typeof value === 'string') {
      return value;
    }
    if (value) {
      return value.name + ' (' + value.tenant.name + ')';
    }
    return '';
  }

  clear(): void {
    this.value = undefined;
    this.filterText$.next('');
    this.trigger$.next('reset');
  }

  goToTeam(): void {
    this.router.navigate(['team', this.value?.id]);
  }

  getTeamTitle(team: TeamItemDto | undefined): string {
    if (!team) {
      return '';
    }
    return team.name + ' (' + team.tenant.name + ')';
  }
}
